/**
 * NetworkEdgesTab - Tab de segmentos de red canónica con códigos H3
 *
 * @version 2.1
 * @created 2026-03-03
 * @updated 2026-03-04 - Codificación H3 R9 activa
 * @module core/layouts/panels/LeftPanel
 *
 * @description
 * Carga segmentos desde la API y genera códigos usando el sistema H3 R9.
 * El código se calcula basándose en el punto medio del segmento.
 * Formato: VE_CELDA_SEQ (ej: VE_AA001_005)
 *
 * @remarks
 * Sistema de codificación:
 * - objectType: 'VE' (Via/Edge)
 * - Coordenadas: punto medio del LineString
 * - Resolución H3: R9 (malla canónica del proyecto)
 * - Fallback: VE-{UUID} si falla la API
 */

import { useState, useEffect, useMemo } from 'react';
import { BaseInventoryTab } from './BaseInventoryTab';
import type { InventoryRow } from './BaseInventoryTab';
import { canonicalNetworkApi } from '../../../spatial/services/canonical-network-api.service';
import type { CanonicalEdge } from '../../../spatial/types';
import { useProject } from '../../../projects';
import { useUrbanNetworkSafe } from '../../../spatial/UrbanNetworkContext';

export interface NetworkEdgesTabProps {
  /**
   * Callback al hacer clic en el código de un segmento
   */
  onCodeClick?: (edgeId: string) => void;

  /**
   * ID del segmento activamente seleccionado
   */
  activeElementId?: string | null;

  /**
   * Texto de búsqueda para filtrar filas por código o nombre
   */
  searchQuery?: string;
}

/**
 * Convierte CanonicalEdge a InventoryRow con código H3
 */
async function edgeToInventoryRow(
  edge: CanonicalEdge,
  projectId: string
): Promise<InventoryRow> {
  // Calcular punto medio del segmento
  // Usar ID corto como código (evitar saturar backend con miles de llamadas API)
  const shortId = edge.id.slice(0, 8).toUpperCase();
  const code = `VE-${shortId}`;

  // Generar nombre descriptivo con clase de vía y longitud
  const roadClassLabels: Record<string, string> = {
    motorway: 'Autopista',
    trunk: 'Troncal',
    primary: 'Principal',
    secondary: 'Secundaria',
    tertiary: 'Terciaria',
    residential: 'Residencial',
    service: 'Servicio',
    unclassified: 'No clasificada',
    unknown: 'Desconocida',
  };

  const roadClassLabel = edge.roadClass !== null
    ? (roadClassLabels[edge.roadClass] || 'Vía')
    : 'Vía';
  // lengthMeters viene como string desde la API, convertir a número
  const lengthNum = typeof edge.lengthMeters === 'string'
    ? parseFloat(edge.lengthMeters)
    : edge.lengthMeters;
  const lengthFormatted = lengthNum !== null ? lengthNum.toFixed(1) : '0.0';
  const name = `${roadClassLabel} (${lengthFormatted}m)`;

  return {
    id: edge.id,
    code,
    name,
  };
}

export function NetworkEdgesTab({
  onCodeClick,
  activeElementId,
  searchQuery = '',
}: NetworkEdgesTabProps): JSX.Element {
  const { projectId } = useProject();
  const urbanNetwork = useUrbanNetworkSafe();
  const [edges, setEdges] = useState<CanonicalEdge[]>([]);
  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Cargar segmentos desde la API
   */
  useEffect(() => {
    if (!projectId) {
      setEdges([]);
      setLoading(false);
      return;
    }

    const loadEdges = async () => {
      setLoading(true);
      setError(null);
      try {
        const edgesData = await canonicalNetworkApi.queryEdges(projectId, {
          status: 'active',
          limit: 100000,
        });
        setEdges(edgesData);
      } catch (err) {
        console.error('[NetworkEdgesTab] Error loading edges:', err);
        setError('Error al cargar segmentos');
      } finally {
        setLoading(false);
      }
    };

    void loadEdges();
  }, [projectId]);

  /**
   * Escuchar evento de actualización de segmentos
   */
  useEffect(() => {
    const handleEdgeUpdated = () => {
      // Recargar segmentos cuando se actualiza uno
      if (projectId) {
        canonicalNetworkApi.queryEdges(projectId, {
          status: 'active',
          limit: 100000,
        }).then(setEdges).catch(console.error);
      }
    };

    window.addEventListener('network-edge-updated', handleEdgeUpdated);
    return () => window.removeEventListener('network-edge-updated', handleEdgeUpdated);
  }, [projectId]);

  /**
   * Convertir segmentos a InventoryRow con códigos H3 asíncronos
   */
  useEffect(() => {
    if (!projectId || edges.length === 0) {
      setRows([]);
      return;
    }

    const generateRows = async () => {
      try {
        const rowPromises = edges.map(edge => edgeToInventoryRow(edge, projectId));
        const generatedRows = await Promise.all(rowPromises);
        setRows(generatedRows);
      } catch (err) {
        console.error('[NetworkEdgesTab] Error generating rows:', err);
        // Fallback: crear rows sin códigos generados
        const fallbackRows: InventoryRow[] = edges.map(edge => {
          const lengthNum = typeof edge.lengthMeters === 'string'
            ? parseFloat(edge.lengthMeters)
            : edge.lengthMeters;
          const lengthFormatted = lengthNum !== null ? lengthNum.toFixed(1) : '0.0';
          return {
            id: edge.id,
            code: `VE-${edge.id.slice(0, 8).toUpperCase()}`,
            name: `Vía (${lengthFormatted}m)`,
          };
        });
        setRows(fallbackRows);
      }
    };

    void generateRows();
  }, [edges, projectId]);

  /**
   * Filtrar filas por código o nombre según searchQuery
   */
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.trim().toLowerCase();
    return rows.filter(row =>
      row.code.toLowerCase().includes(q) ||
      row.name.toLowerCase().includes(q)
    );
  }, [rows, searchQuery]);

  /**
   * Handler para hover sobre filas en el tab
   * Actualiza el contexto de red urbana para highlight visual en mapa
   */
  const handleRowHover = (edgeId: string | null): void => {
    if (urbanNetwork) {
      urbanNetwork.setHoveredEdgeId(edgeId);
    }
  };

  if (loading) {
    return (
      <BaseInventoryTab
        title="Segmentos"
        rows={[]}
        emptyMessage="Cargando segmentos..."
        eventPrefix="network-edges"
        moduleId="urban-network"
        onCodeClick={onCodeClick}
        activeElementId={activeElementId}
        onRowHover={handleRowHover}
      />
    );
  }

  if (error) {
    return (
      <BaseInventoryTab
        title="Segmentos"
        rows={[]}
        emptyMessage={error}
        eventPrefix="network-edges"
        moduleId="urban-network"
        onCodeClick={onCodeClick}
        activeElementId={activeElementId}
        onRowHover={handleRowHover}
      />
    );
  }

  return (
    <BaseInventoryTab
      title="Segmentos"
      rows={filteredRows}
      emptyMessage={searchQuery.trim() ? 'Sin resultados para la búsqueda' : 'Sin segmentos registrados'}
      eventPrefix="network-edges"
      moduleId="urban-network"
      onCodeClick={onCodeClick}
      activeElementId={activeElementId}
      onRowHover={handleRowHover}
    />
  );
}
